// futureStops defaults true: an unassign that leaves next week's occurrence isn't permanent.
// No master read, unlike `sequenceMasterSlot`: these apply as a removal set, so an absent slot drops out.
import chunk from 'lodash/chunk'

import { err, ok } from 'types/entities/shared/result'

import { extractBackendError } from '../backendError'
import { collectRouteSlotIds } from '../slotPayloads'

import type { SlotRow } from '../slotPayloads'
import type { StepResult } from '../types'
import type { ApiDayOfWeek } from 'types/api/route/masterStopRequests'
import type { DayOfWeek, RouteId } from 'types/entities/shared/ids'
import type { Deps, PipelineStep } from 'types/modules/routing'

const BATCH_SIZE = 200

export interface UnassignMasterSlotStepArgs {
  readonly routeId: RouteId
  readonly dayOfWeek: DayOfWeek
  readonly rows: ReadonlyArray<SlotRow>
  readonly futureStops?: boolean
}

export type UnassignMasterSlotStepValue = {
  readonly stopCount: number
  readonly batchCount: number
}

export type UnassignMasterSlotStepResult = StepResult<'UNASSIGN_MASTER', UnassignMasterSlotStepValue>

export const makeUnassignMasterSlotStep = (
  args: UnassignMasterSlotStepArgs,
  deps: Deps
): PipelineStep<'UNASSIGN_MASTER', UnassignMasterSlotStepResult> => {
  const stepId = `unassign-master-slot:${args.routeId}:${args.dayOfWeek}`
  return {
    id: stepId,
    phase: 'UNASSIGN_MASTER',
    label: 'Failed to unassign stops from master sequence',
    applyOrder: 200,
    run: async ctx => {
      if (!deps.unassignMasterSlot) {
        return err({ kind: 'PRECONDITION_FAILED', reason: 'unassignMasterSlot dep not provided — the v3-slot strategy requires it' })
      }
      const routeSlotIds = collectRouteSlotIds(args.rows)
      if (routeSlotIds.length === 0) {
        return ok({ phase: 'UNASSIGN_MASTER', stepId, value: { stopCount: 0, batchCount: 0 } })
      }

      const batches = chunk(routeSlotIds as ReadonlyArray<string>, BATCH_SIZE)
      let stopCount = 0
      try {
        // eslint-disable-next-line no-restricted-syntax -- batches apply in order; an array method would fan them out
        for (const batch of batches) {
          // eslint-disable-next-line no-await-in-loop -- sequential by design, see above
          const response = await deps
            .unassignMasterSlot({
              routeId: args.routeId,
              data: { dayOfWeek: args.dayOfWeek as ApiDayOfWeek, futureStops: args.futureStops ?? true, stops: [...batch] },
              skipInvalidate: true,
            })
            .unwrap()
          stopCount += response?.stopCount ?? 0
          ctx.recordCommit()
        }
        return ok({ phase: 'UNASSIGN_MASTER', stepId, value: { stopCount, batchCount: batches.length } })
      } catch (cause: unknown) {
        const { status, message } = extractBackendError(cause)
        return err({ kind: 'BE_REJECTED', phase: 'UNASSIGN_MASTER', status, message })
      }
    },
  }
}
